import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Avatar, Empty } from "../components/ui";
import { relDate } from "../lib/format";
import type { Survey, SurveyResponse, SurveyType, SurveyStatus } from "../lib/types";
import * as work from "../lib/workClient";

const TYPE_META: Record<SurveyType, { label: string; scale: string; max: number; min: number; hint: string }> = {
  nps: { label: "NPS", scale: "0–10", max: 10, min: 0, hint: "Net Promoter Score" },
  csat: { label: "CSAT", scale: "1–5", max: 5, min: 1, hint: "Customer Satisfaction" },
  ces: { label: "CES", scale: "1–7", max: 7, min: 1, hint: "Customer Effort Score" },
};
const STATUS_LABEL: Record<SurveyStatus, string> = { draft: "Draft", active: "Active", closed: "Closed" };
const STATUS_CLASS: Record<SurveyStatus, string> = { draft: "badge--neutral", active: "badge--good", closed: "badge--neutral" };

/** Roll a survey's responses up into a headline metric + distribution. */
function score(type: SurveyType, rs: SurveyResponse[]) {
  const n = rs.length;
  if (type === "nps") {
    const prom = rs.filter((r) => r.score >= 9).length;
    const pass = rs.filter((r) => r.score >= 7 && r.score <= 8).length;
    const det = rs.filter((r) => r.score <= 6).length;
    const nps = n ? Math.round(((prom - det) / n) * 100) : 0;
    return {
      headline: n ? `${nps > 0 ? "+" : ""}${nps}` : "—",
      caption: "NPS",
      good: nps >= 30,
      segments: [
        { label: "Promoters", count: prom, color: "var(--good)" },
        { label: "Passives", count: pass, color: "var(--corona)" },
        { label: "Detractors", count: det, color: "var(--risk)" },
      ],
    };
  }
  if (type === "csat") {
    const top = rs.filter((r) => r.score >= 4).length;
    const csat = n ? Math.round((top / n) * 100) : 0;
    return {
      headline: n ? `${csat}%` : "—",
      caption: "Satisfied (4–5)",
      good: csat >= 80,
      segments: [
        { label: "Satisfied (4–5)", count: top, color: "var(--good)" },
        { label: "Neutral (3)", count: rs.filter((r) => r.score === 3).length, color: "var(--corona)" },
        { label: "Unsatisfied (1–2)", count: rs.filter((r) => r.score <= 2).length, color: "var(--risk)" },
      ],
    };
  }
  const avg = n ? rs.reduce((s, r) => s + r.score, 0) / n : 0;
  return {
    headline: n ? avg.toFixed(1) : "—",
    caption: "Avg effort (lower is better)",
    good: avg > 0 && avg <= 3,
    segments: [
      { label: "Low effort (1–3)", count: rs.filter((r) => r.score <= 3).length, color: "var(--good)" },
      { label: "Moderate (4–5)", count: rs.filter((r) => r.score >= 4 && r.score <= 5).length, color: "var(--corona)" },
      { label: "High effort (6–7)", count: rs.filter((r) => r.score >= 6).length, color: "var(--risk)" },
    ],
  };
}

export default function Surveys() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [surveys, setSurveys] = useState<Survey[]>(ws.surveys);
  const [responses, setResponses] = useState<SurveyResponse[]>(ws.surveyResponses);
  const [selId, setSelId] = useState<string>(ws.surveys[0]?.id ?? "");
  const [composing, setComposing] = useState(false);

  useEffect(() => {
    work.loadSurveys(ws).then((r) => {
      setMode(r.mode); setSurveys(r.surveys); setResponses(r.responses);
      if (!r.surveys.some((s) => s.id === selId)) setSelId(r.surveys[0]?.id ?? "");
    });
  }, [ws]);

  const survey = surveys.find((s) => s.id === selId) ?? null;
  const rs = useMemo(() => responses.filter((r) => r.surveyId === selId).sort((a, b) => +new Date(b.at) - +new Date(a.at)), [responses, selId]);
  const roll = survey ? score(survey.type, rs) : null;

  const patch = async (p: { name?: string; question?: string; status?: SurveyStatus }) => {
    if (!survey) return;
    setSurveys((prev) => prev.map((s) => (s.id === survey.id ? { ...s, ...p } : s)));
    await work.updateSurvey(mode, ws, survey.id, p);
  };
  const del = async (s: Survey) => {
    setSurveys((prev) => prev.filter((x) => x.id !== s.id));
    setResponses((prev) => prev.filter((r) => r.surveyId !== s.id));
    if (selId === s.id) setSelId(surveys.find((x) => x.id !== s.id)?.id ?? "");
    await work.deleteSurvey(mode, ws, s.id);
  };
  const create = async (input: { name: string; type: SurveyType; question: string }) => {
    const s = await work.createSurvey(mode, ws, input);
    setSurveys((prev) => [...prev, s]);
    setSelId(s.id); setComposing(false);
  };
  const record = async (input: { accountId: string; score: number; respondent?: string; comment?: string }) => {
    if (!survey) return;
    const r = await work.addResponse(mode, ws, { surveyId: survey.id, ...input });
    setResponses((prev) => [r, ...prev]);
  };

  const totalResponses = responses.length;
  const activeCount = surveys.filter((s) => s.status === "active").length;

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Surveys</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Voice of the customer</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>NPS, CSAT and effort programs — every response tied to the account it came from, rolled up into a live score.</p>
        </div>
        <div className="row" style={{ gap: 10, alignItems: "center" }}>
          <ModePill mode={mode} />
          <button className="btn btn--primary" onClick={() => setComposing(true)}><Icon name="plus" size={15} /> New survey</button>
        </div>
      </div>

      <div className="stats">
        <Stat icon="star" label="Programs" value={String(surveys.length)} foot={`${activeCount} active`} />
        <Stat icon="check" label="Responses collected" value={String(totalResponses)} foot="Across all surveys" />
        <Stat icon="pulse" label={roll ? roll.caption : "Score"} value={roll ? roll.headline : "—"} foot={survey ? `${TYPE_META[survey.type].label} · ${rs.length} responses` : "Select a survey"} color={roll ? (roll.good ? "var(--good)" : "var(--watch)") : undefined} />
      </div>

      <div className="auto-layout">
        <div className="card">
          <div className="card__hd"><div><h3>Surveys</h3><span className="sub">{surveys.length} programs</span></div></div>
          <div className="card__bd" style={{ display: "grid", gap: 8 }}>
            {surveys.length === 0 ? <Empty>No surveys yet.</Empty> : surveys.map((s) => {
              const active = s.id === selId;
              return (
                <button key={s.id} className={`svy-item ${active ? "svy-item--on" : ""}`} onClick={() => setSelId(s.id)}>
                  <div className="row between" style={{ gap: 8 }}>
                    <b style={{ fontSize: 13.5 }}>{s.name}</b>
                    <span className={`badge ${STATUS_CLASS[s.status]}`}>{STATUS_LABEL[s.status]}</span>
                  </div>
                  <div className="row" style={{ gap: 8, marginTop: 6 }}>
                    <span className="chip">{TYPE_META[s.type].label}</span>
                    <span className="faint" style={{ fontSize: 12 }}>{responses.filter((r) => r.surveyId === s.id).length} responses</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {survey && roll ? (
          <div style={{ display: "grid", gap: 16 }}>
            <div className="card">
              <div className="card__hd">
                <div>
                  <div className="row" style={{ gap: 8 }}>
                    <input className="inp inp--sm" style={{ fontWeight: 700, fontSize: 15, minWidth: 220 }} value={survey.name} onChange={(e) => patch({ name: e.target.value })} />
                    <span className="chip">{TYPE_META[survey.type].label} · {TYPE_META[survey.type].scale}</span>
                  </div>
                  <span className="sub">{TYPE_META[survey.type].hint}</span>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <select className="inp inp--sm" value={survey.status} onChange={(e) => patch({ status: e.target.value as SurveyStatus })}>
                    {(["draft", "active", "closed"] as SurveyStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                  <button className="btn btn--ghost btn--sm" onClick={() => del(survey)} title="Delete survey"><Icon name="dot" size={14} /> Delete</button>
                </div>
              </div>
              <div className="card__bd">
                <input className="inp" style={{ width: "100%" }} value={survey.question} onChange={(e) => patch({ question: e.target.value })} placeholder="Survey question" />
                <div className="row" style={{ gap: 24, marginTop: 18, alignItems: "center", flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontFamily: "var(--serif)", fontSize: 40, lineHeight: 1, color: roll.good ? "var(--good)" : "var(--watch)" }}>{roll.headline}</div>
                    <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>{roll.caption} · {rs.length} responses</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div className="forecast-bar">
                      {roll.segments.filter((sg) => sg.count > 0).map((sg) => (
                        <span key={sg.label} className="forecast-seg" style={{ width: `${(sg.count / (rs.length || 1)) * 100}%`, background: sg.color }} title={`${sg.label}: ${sg.count}`} />
                      ))}
                    </div>
                    <div className="row" style={{ gap: 16, marginTop: 10, flexWrap: "wrap" }}>
                      {roll.segments.map((sg) => (
                        <span key={sg.label} className="row" style={{ gap: 7, fontSize: 12 }}>
                          <span style={{ width: 9, height: 9, borderRadius: 3, background: sg.color }} />
                          <b>{sg.label}</b> <span className="faint">{sg.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <RecordForm survey={survey} accounts={ws.accounts} onRecord={record} />

            <div className="card">
              <div className="card__hd"><div><h3>Responses</h3><span className="sub">{rs.length} for this survey</span></div></div>
              <div style={{ overflowX: "auto" }}>
                {rs.length === 0 ? <div className="card__bd"><Empty>No responses yet — record one above.</Empty></div> : (
                  <table className="table">
                    <thead><tr><th>Account</th><th>Score</th><th>Respondent</th><th>Comment</th><th>When</th></tr></thead>
                    <tbody>
                      {rs.map((r) => (
                        <tr key={r.id}>
                          <td><Link to={`/accounts/${r.accountId}`} className="acct-cell"><Avatar name={r.accountName} size="sm" /><div><b>{r.accountName}</b></div></Link></td>
                          <td><span className={`badge ${badgeFor(survey.type, r.score)}`}>{r.score}</span></td>
                          <td className="faint" style={{ fontSize: 12.5 }}>{r.respondent ?? "—"}</td>
                          <td style={{ fontSize: 12.5, maxWidth: 320 }}>{r.comment ?? <span className="faint">—</span>}</td>
                          <td className="faint" style={{ fontSize: 12 }}>{relDate(r.at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        ) : <div className="card"><div className="card__bd"><Empty>Select or create a survey to see its results.</Empty></div></div>}
      </div>

      {composing && <NewSurvey onClose={() => setComposing(false)} onCreate={create} />}
    </div>
  );
}

function badgeFor(type: SurveyType, s: number) {
  if (type === "nps") return s >= 9 ? "badge--good" : s <= 6 ? "badge--risk" : "badge--neutral";
  if (type === "csat") return s >= 4 ? "badge--good" : s <= 2 ? "badge--risk" : "badge--neutral";
  return s <= 3 ? "badge--good" : s >= 6 ? "badge--risk" : "badge--neutral";
}

function RecordForm({ survey, accounts, onRecord }: { survey: Survey; accounts: { id: string; name: string }[]; onRecord: (i: { accountId: string; score: number; respondent?: string; comment?: string }) => void }) {
  const meta = TYPE_META[survey.type];
  const [accountId, setAccountId] = useState(accounts[0]?.id ?? "");
  const [scoreVal, setScoreVal] = useState<number>(meta.max);
  const [respondent, setRespondent] = useState("");
  const [comment, setComment] = useState("");
  const submit = () => {
    if (!accountId) return;
    onRecord({ accountId, score: Math.max(meta.min, Math.min(meta.max, scoreVal)), respondent: respondent.trim() || undefined, comment: comment.trim() || undefined });
    setComment(""); setRespondent("");
  };
  return (
    <div className="card">
      <div className="card__hd"><div><h3>Record a response</h3><span className="sub">Log a reply on {meta.scale} scale</span></div></div>
      <div className="card__bd" style={{ display: "grid", gap: 12 }}>
        <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
          <label className="labeled" style={{ flex: 2, minWidth: 180 }}><span className="labeled__l">Account</span>
            <select className="inp" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          <label className="labeled" style={{ width: 120 }}><span className="labeled__l">Score ({meta.scale})</span>
            <input className="inp" type="number" min={meta.min} max={meta.max} value={scoreVal} onChange={(e) => setScoreVal(Number(e.target.value))} />
          </label>
          <label className="labeled" style={{ flex: 1, minWidth: 140 }}><span className="labeled__l">Respondent</span>
            <input className="inp" value={respondent} onChange={(e) => setRespondent(e.target.value)} placeholder="e.g. Champion" />
          </label>
        </div>
        <label className="labeled"><span className="labeled__l">Comment (optional)</span>
          <input className="inp" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Verbatim feedback" />
        </label>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn btn--primary btn--sm" onClick={submit} disabled={!accountId}>Add response</button>
        </div>
      </div>
    </div>
  );
}

function NewSurvey({ onClose, onCreate }: { onClose: () => void; onCreate: (i: { name: string; type: SurveyType; question: string }) => void }) {
  const [name, setName] = useState("");
  const [type, setType] = useState<SurveyType>("nps");
  const [question, setQuestion] = useState("How likely are you to recommend us to a colleague?");
  const submit = () => { if (name.trim() && question.trim()) onCreate({ name: name.trim(), type, question: question.trim() }); };
  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="compose-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="compose-modal__hd">
          <div className="row" style={{ gap: 8 }}><Icon name="star" size={16} /><b>New survey</b></div>
          <button className="icon-btn" onClick={onClose}><Icon name="dot" size={16} /></button>
        </div>
        <div className="compose-modal__bd" style={{ display: "grid", gap: 14 }}>
          <label className="labeled"><span className="labeled__l">Name</span>
            <input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Quarterly NPS" autoFocus />
          </label>
          <label className="labeled"><span className="labeled__l">Type</span>
            <select className="inp" value={type} onChange={(e) => setType(e.target.value as SurveyType)}>
              {(Object.keys(TYPE_META) as SurveyType[]).map((t) => <option key={t} value={t}>{TYPE_META[t].label} — {TYPE_META[t].hint} ({TYPE_META[t].scale})</option>)}
            </select>
          </label>
          <label className="labeled"><span className="labeled__l">Question</span>
            <input className="inp" value={question} onChange={(e) => setQuestion(e.target.value)} />
          </label>
        </div>
        <div className="compose-modal__ft">
          <button className="btn btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" onClick={submit} disabled={!name.trim() || !question.trim()}>Create survey</button>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, foot, color }: { icon: string; label: string; value: string; foot: string; color?: string }) {
  return (
    <div className="stat">
      <div className="stat__label"><Icon name={icon} size={14} /> {label}</div>
      <div className="stat__value" style={{ color, fontSize: 24 }}>{value}</div>
      <div className="stat__foot">{foot}</div>
    </div>
  );
}
function ModePill({ mode }: { mode: work.Mode }) {
  return <span className={`mode-pill ${mode === "api" ? "live" : ""}`} title={mode === "api" ? "Connected to the Penumbrai API" : "Standalone demo — changes saved to this browser"}><i />{mode === "api" ? "Live API" : "Local demo"}</span>;
}
